import type { ToolDefinition } from '../../types/editor.types';

export const BUILT_IN_TOOLS: ToolDefinition[] = [
  {
    id: 'heading',
    label: 'Heading',
    icon: 'Heading',
    category: 'content',
    supportedModes: ['email', 'page', 'popup', 'document'],
    defaultContent: { text: 'Your Heading Here', level: 'h2' },
    defaultStyles: {
      fontSize: '28px', fontWeight: '700', color: '#0f172a',
      textAlign: 'left', padding: '8px 0', lineHeight: '1.3',
    },
  },
  {
    id: 'text',
    label: 'Text',
    icon: 'Type',
    category: 'content',
    supportedModes: ['email', 'page', 'popup', 'document'],
    defaultContent: { html: '<p>Add your text here. Click to edit.</p>' },
    defaultStyles: {
      fontSize: '14px', color: '#475569', lineHeight: '1.6', padding: '4px 0',
    },
  },
  {
    id: 'paragraph',
    label: 'Paragraph',
    icon: 'AlignLeft',
    category: 'content',
    supportedModes: ['email', 'page', 'document'],
    defaultContent: { text: 'Your paragraph text goes here. Click to edit this block and add your own content.' },
    defaultStyles: {
      fontSize: '14px', color: '#475569', lineHeight: '1.7', padding: '8px 0',
    },
  },
  {
    id: 'button',
    label: 'Button',
    icon: 'MousePointerClick',
    category: 'content',
    supportedModes: ['email', 'page', 'popup'],
    defaultContent: { text: 'Click Here', href: '#', target: '_blank' },
    defaultStyles: {
      backgroundColor: '#6366f1', color: '#ffffff',
      padding: '12px 24px', borderRadius: '6px',
      fontSize: '14px', fontWeight: '600', textAlign: 'center',
      display: 'inline-block',
    },
  },
  {
    id: 'image',
    label: 'Image',
    icon: 'Image',
    category: 'media',
    supportedModes: ['email', 'page', 'popup', 'document'],
    defaultContent: { src: '', alt: '', href: '', width: '100%' },
    defaultStyles: { textAlign: 'center', padding: '8px 0' },
  },
  {
    id: 'video',
    label: 'Video',
    icon: 'Play',
    category: 'media',
    supportedModes: ['page', 'popup'],
    defaultContent: { src: '', provider: 'youtube', thumbnail: '' },
    defaultStyles: { padding: '8px 0', textAlign: 'center' },
  },
  {
    id: 'divider',
    label: 'Divider',
    icon: 'Minus',
    category: 'layout',
    supportedModes: ['email', 'page', 'popup', 'document'],
    defaultContent: {},
    defaultStyles: {
      borderTopWidth: '1px', borderTopStyle: 'solid',
      borderTopColor: '#e2e8f0', margin: '16px 0',
    },
  },
  {
    id: 'columns',
    label: 'Columns',
    icon: 'Columns',
    category: 'layout',
    supportedModes: ['email', 'page', 'document'],
    defaultContent: { columnCount: 2, gap: '16px' },
    defaultStyles: { padding: '8px 0' },
  },
  {
    id: 'form',
    label: 'Form',
    icon: 'FormInput',
    category: 'interactive',
    supportedModes: ['page', 'popup'],
    defaultContent: {
      fields: [
        { id: 'name', type: 'text', label: 'Name', placeholder: 'Your name', required: true },
        { id: 'email', type: 'email', label: 'Email', placeholder: 'your@email.com', required: true },
      ],
      submitText: 'Submit',
      action: '#',
    },
    defaultStyles: { padding: '16px 0' },
  },
  {
    id: 'timer',
    label: 'Countdown',
    icon: 'Timer',
    category: 'interactive',
    supportedModes: ['email', 'page', 'popup'],
    defaultContent: {
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      labels: { days: 'Days', hours: 'Hours', minutes: 'Minutes', seconds: 'Seconds' },
    },
    defaultStyles: { textAlign: 'center', padding: '16px 0' },
  },
  {
    id: 'social',
    label: 'Social',
    icon: 'Share2',
    category: 'social',
    supportedModes: ['email', 'page'],
    defaultContent: {
      platforms: [
        { name: 'facebook', url: 'https://facebook.com' },
        { name: 'twitter', url: 'https://twitter.com' },
        { name: 'instagram', url: 'https://instagram.com' },
        { name: 'linkedin', url: 'https://linkedin.com' },
      ],
      iconSize: 32, layout: 'horizontal',
    },
    defaultStyles: { textAlign: 'center', padding: '8px 0' },
  },
  {
    id: 'menu',
    label: 'Menu',
    icon: 'Menu',
    category: 'content',
    supportedModes: ['email', 'page'],
    defaultContent: {
      items: [
        { label: 'Home', href: '#' },
        { label: 'About', href: '#' },
        { label: 'Contact', href: '#' },
      ],
      layout: 'horizontal',
    },
    defaultStyles: {
      textAlign: 'center', padding: '12px 0',
      fontSize: '14px', color: '#475569',
    },
  },
  {
    id: 'html',
    label: 'HTML',
    icon: 'Code',
    category: 'advanced',
    supportedModes: ['email', 'page', 'popup'],
    defaultContent: { code: '<div style="padding: 16px;">Custom HTML goes here</div>' },
    defaultStyles: {},
  },
  {
    id: 'table',
    label: 'Table',
    icon: 'Table',
    category: 'advanced',
    supportedModes: ['email', 'page', 'document'],
    defaultContent: {
      headers: ['Column 1', 'Column 2', 'Column 3'],
      rows: [['Cell 1', 'Cell 2', 'Cell 3'], ['Cell 4', 'Cell 5', 'Cell 6']],
    },
    defaultStyles: { padding: '8px 0', fontSize: '14px' },
  },
  {
    id: 'carousel',
    label: 'Carousel',
    icon: 'GalleryHorizontal',
    category: 'media',
    supportedModes: ['email', 'page'],
    defaultContent: {
      images: [
        { src: '', alt: 'Slide 1', href: '' },
        { src: '', alt: 'Slide 2', href: '' },
      ],
      autoplay: false,
    },
    defaultStyles: { padding: '8px 0', textAlign: 'center' },
  },
];

export const TOOL_CATEGORIES = [
  { id: 'content', label: 'Content' },
  { id: 'layout', label: 'Layout' },
  { id: 'media', label: 'Media' },
  { id: 'interactive', label: 'Interactive' },
  { id: 'social', label: 'Social' },
  { id: 'advanced', label: 'Advanced' },
] as const;

export const getToolById = (id: string) =>
  BUILT_IN_TOOLS.find((t) => t.id === id);

export const getToolsByCategory = (category: string, mode?: string) =>
  BUILT_IN_TOOLS.filter(
    (t) => t.category === category &&
    (mode ? t.supportedModes.includes(mode as never) : true)
  );
